import { CategoryChartQueryFn, CategoryChartQueryParam } from "../categoryChart";
import {
    deltaValue,
    getBaseValue,
    // getKpiReportFromGQResult, KpiReport, KpiReportEntity,
    roundValue,
    Unit
} from "../processors";
import {
    // queryEntities,
    SEPARATOR,
    VALUE_STATUS_NAMING
} from "../misc";
import { figureDeltaString } from "./figureDeltaString";
import _ from "lodash";
import { FigureHorStackBarKpiProviders } from "./categoryChartData.types";
import { getKpisForProvidersWithCategoryChart } from "../opendata";

const kpiCodeSchemeTypeToKey = (kpiCode: string, schemeType: string) => {
    return _.join([kpiCode, schemeType], SEPARATOR);
};

export const fetchFigureHorStackBarKpiComparisonChartData: CategoryChartQueryFn = async ({
    categoryChart,
    providers,
    kpis,
    category
}: CategoryChartQueryParam): Promise<FigureHorStackBarKpiProviders> => {
    if (!providers || !providers.length || !categoryChart || kpis == null || !kpis?.length) return null;

    const _kpiReportResults = await getKpisForProvidersWithCategoryChart(kpis, providers, false, categoryChart);

    if (!_kpiReportResults) return null;

    const summaryKpis = kpis?.filter(d => d?.[3]?.toString()?.indexOf("useAsSummary") > -1);
    const summaryKpiMap = _.keyBy(summaryKpis, d => d?.[0]);
    const otherLabel = _.uniq(summaryKpis.map(d => d?.[3]).map(d => d?.split("_")?.[1]))?.find(
        d => d != null && d.toString().trim() !== ""
    );
    const elementKpis = kpis?.filter(d => (d?.[3] ?? "")?.indexOf("useAsSummary") < 0);
    const elementKpiMap = _.keyBy(elementKpis, d => d?.[0]);

    const kpiReportResults = (_kpiReportResults ?? []).filter(
        d => elementKpiMap[d.kpiCode] != null && d != null && d.provider != null
    );

    const summaryKpiReportResults = (_kpiReportResults ?? []).filter(
        d => summaryKpiMap[d.kpiCode] != null && d != null && d.provider != null
    );

    // year data
    let unit = _.uniq(
        kpiReportResults?.map(d => d.unit)?.filter(d => d != null && d.toString().trim() !== "") ?? []
    )?.[0] as Unit;

    const years = _.uniq(_.orderBy(kpiReportResults?.map(d => d.year) ?? [], d => d, "desc")) ?? [];

    // const startYear = years[years.length - 1];
    const latestYear = years[0];
    const previousYear = years[1];

    if (latestYear == null) return null;

    const { displayTitle, visHeader, visFooter, figureHeader, figureFooter, visType } = categoryChart;

    // this structure is to extract kpi chart label names
    const kpiCodeSchemeTypeToKpiMetrics = _.keyBy(kpis, kpi => {
        return _.join([kpi[0], kpi[1]], SEPARATOR);
    });

    const metricKeyToResult = _.keyBy(
        kpiReportResults.filter(
            d =>
                d.indicatorTitle != null &&
                d.indicatorTitle.toString().trim() !== "" &&
                d.kpiCode != null &&
                d.kpiCode.toString().trim() !== "" &&
                d.schemeType != null &&
                d.schemeType.toString().trim() !== ""
        ),
        d => kpiCodeSchemeTypeToKey(d.kpiCode, d.schemeType)
    );

    const kpiMetadata: Record<
        string,
        {
            label: string;
            seriesName: string;
            key: string;
            kpiCode: string;
            schemeType: string;
        }
    > = _.keyBy(
        kpis?.map(d => {
            const key = kpiCodeSchemeTypeToKey(d[0], d[1]) ?? "";

            return {
                key,
                kpiCode: d[0],
                schemeType: d[1],
                label: d[2] ?? metricKeyToResult?.[key]?.indicatorTitle,
                seriesName: d[2] ?? metricKeyToResult?.[key]?.indicatorTitle
            };
        }) ?? [],
        d => d.key
    );

    // kpi report results for the provider
    let orderedKpis: string[][] = undefined;
    const providerTidToResults = _.groupBy(kpiReportResults, d => d?.provider?.tid);
    const providerTidToSummaryResults = _.groupBy(summaryKpiReportResults, d => d?.provider?.tid);
    const providerTidToMeta = _.keyBy(
        (providers ?? []).map((provider, index) => {
            return {
                index,
                tid: provider.tid
            };
        }),
        d => d.tid
    );
    const elements =
        providers?.map(provider => {
            const tid = provider?.tid;
            const providerExist = providerTidToResults?.[tid];
            const results = providerTidToResults?.[tid] ?? [];
            const summaryResults = providerTidToSummaryResults?.[tid] ?? [];
            const yearsToResults = _.groupBy(results, res => res.year);
            const yearsToSummaryResults = _.groupBy(summaryResults, res => res.year);

            // it should have resuls of metrics of years
            const reports = (yearsToResults?.[latestYear] ?? []).filter(d => d.value != null);
            const nullValueReports = (yearsToResults?.[latestYear] ?? []).filter(d => d.value == null);
            const prevReports = (yearsToResults?.[previousYear ?? ""] ?? []).filter(d => d.value != null);

            const summaryReports = (yearsToSummaryResults?.[latestYear] ?? []).filter(d => d.value != null);
            // const nullValueSummaryReports = (yearsToSummaryResults?.[latestYear] ?? []).filter(d => d.value == null);
            const prevSummaryReports = (yearsToSummaryResults?.[previousYear ?? ""] ?? []).filter(d => d.value != null);

            // if it does not have results of the latest year, won't display the chart
            if (!reports || !reports.length) {
                const figureHeaderProcessed = _.template(figureHeader?.value ?? "")({
                    year: latestYear,
                    value: null,
                    deltaString: null
                });

                const figureFooterProcessed = _.template(figureFooter?.value ?? "")({
                    year: latestYear,
                    value: null,
                    deltaString: null
                });

                const figure = {
                    decimals: 0,
                    header:
                        !figureHeader || figureHeader.processed.trim() === ""
                            ? null
                            : {
                                  processed: figureHeaderProcessed,
                                  value: figureHeaderProcessed,
                                  format: figureHeaderProcessed
                              },
                    footer:
                        !figureFooter || figureFooter.processed.trim() === ""
                            ? null
                            : {
                                  processed: figureFooterProcessed,
                                  value: figureFooterProcessed,
                                  format: figureFooterProcessed
                              },
                    value:
                        nullValueReports?.[0]?.valueStatus != null
                            ? VALUE_STATUS_NAMING?.[nullValueReports?.[0]?.valueStatus] ??
                              nullValueReports?.[0]?.valueStatus
                            : !providerExist
                            ? VALUE_STATUS_NAMING.NR
                            : prevReports?.length > 0 && !reports?.length
                            ? VALUE_STATUS_NAMING.MD
                            : VALUE_STATUS_NAMING.NR
                };

                const graph = null;

                return {
                    providerId: tid,
                    figure,
                    graph
                };
            }

            if (!orderedKpis)
                orderedKpis = _.uniqBy(
                    [
                        ...(_.orderBy(reports.filter(d => d != null) ?? [], d => d.value ?? 0, "desc")?.map(d => [
                            d.kpiCode,
                            d.schemeType
                        ]) ?? []),
                        ...(kpis?.map(d => [d[0] ?? "", d[1] ?? ""]) ?? [])
                    ],
                    d => {
                        const kpiCode = d[0] ?? "";
                        const schemeType = d[1] ?? "";
                        return kpiCodeSchemeTypeToKey(kpiCode, schemeType);
                    }
                ).filter(d => {
                    // only select kpis that are required to display
                    return kpiCodeSchemeTypeToKpiMetrics?.[kpiCodeSchemeTypeToKey(d[0] ?? "", d[1] ?? "")] != null;
                });

            const metricToReports = _.keyBy(reports, d => kpiCodeSchemeTypeToKey(d.kpiCode, d.schemeType));

            // for each kpi, build graph data
            const graphData =
                orderedKpis?.map(d => {
                    const kpiCode = d[0];
                    const schemeType = d[1];
                    const key = kpiCodeSchemeTypeToKey(kpiCode, schemeType);

                    // retrieve raw data from the dictionary
                    const atMetricResults = metricToReports[key];

                    const baseValue = getBaseValue(atMetricResults?.value, atMetricResults?.unit);
                    if (baseValue?.value != null) unit = baseValue?.unit;

                    return {
                        value: baseValue?.value ?? 0,
                        label: kpiMetadata?.[key]?.label ?? "",
                        seriesName: kpiMetadata?.[key]?.seriesName
                    };
                }) ?? [];

            // build char header, footer
            const initialYear = years[years?.length - 1];

            const visHeaderProcessed = _.template(visHeader?.value ?? "")({
                startYear: initialYear,
                endYear: latestYear
            });

            const visFooterProcessed = _.template(visFooter?.value ?? "")({
                startYear: initialYear,
                endYear: latestYear
            });

            // build figure data
            const currentYearValues = (summaryReports?.length > 0 ? summaryReports : reports)
                .map(d => getBaseValue(d?.value, d?.unit))
                .filter(d => d.value != null);
            const previousYearValues = (prevSummaryReports?.length > 0 ? prevSummaryReports : prevReports)
                .map(d => getBaseValue(d?.value, d?.unit))
                .filter(d => d.value != null);

            const figureValue = currentYearValues.length > 0 ? _.sumBy(currentYearValues, d => d.value ?? 0) ?? 0 : 0;

            // in case if element values do not add up to the sum, given the summary KPI
            if (figureValue != null && figureValue > _.sumBy(graphData, d => d.value ?? 0)) {
                const all = _.sumBy(graphData, d => d.value ?? 0);
                graphData.push({
                    value: figureValue - all,
                    label: otherLabel ?? "Other",
                    seriesName: otherLabel ?? "Other"
                });
            }

            const prevFigureValue =
                previousYearValues.length > 0 ? _.sumBy(previousYearValues, d => d.value ?? 0) ?? 0 : 0;

            // const figureValue = _.sumBy(reports, d => d.value ?? 0) ?? 0;
            // const prevFigureValue = _.sumBy(prevReports, d => d.value ?? 0) ?? 0;
            const delta = deltaValue(prevFigureValue, figureValue, unit);

            const deltaString = figureDeltaString(delta, category, providerTidToMeta?.[tid]?.index);

            const figureHeaderProcessed = _.template(figureHeader?.value ?? "")({
                year: latestYear,
                value: figureValue,
                deltaString
            });

            const figureFooterProcessed = _.template(figureFooter?.value ?? "")({
                year: latestYear,
                value: figureValue,
                deltaString
            });

            const figure = !(figureValue != null && (visType === "figure" || visType === "figureWithStackHorBarChart"))
                ? null
                : {
                      decimals: roundValue(figureValue, unit)?.decimals,
                      unit: roundValue(figureValue, unit)?.unit as Unit,
                      header:
                          !figureHeader || figureHeader.processed.trim() === ""
                              ? null
                              : {
                                    processed: figureHeaderProcessed,
                                    value: figureHeaderProcessed,
                                    format: figureHeaderProcessed
                                },
                      footer:
                          !figureFooter || figureFooter.processed.trim() === ""
                              ? null
                              : {
                                    processed: figureFooterProcessed,
                                    value: figureFooterProcessed,
                                    format: figureFooterProcessed
                                },
                      value: roundValue(figureValue, unit)?.value
                  };

            const graph =
                visType === "figure"
                    ? null
                    : {
                          data: graphData,
                          header:
                              !visHeader || visHeader.processed.trim() === ""
                                  ? null
                                  : {
                                        processed: visHeaderProcessed,
                                        value: visHeaderProcessed,
                                        format: visHeaderProcessed
                                    },
                          footer:
                              !visFooter || visFooter.processed.trim() === ""
                                  ? null
                                  : {
                                        processed: visFooterProcessed,
                                        value: visFooterProcessed,
                                        format: visFooterProcessed
                                    }
                      };

            return {
                providerId: tid,
                figure,
                graph
            };
        }) ?? [];

    // kpi report results for the provider
    const ret: FigureHorStackBarKpiProviders = {
        title: displayTitle,
        waterProviders: providers,
        unit,
        year: latestYear,
        elements,
        footer: null
    };

    return ret;
};
